import { useMemo } from "react";

import dayjs from "dayjs";

import type { ConversionRatesCountriesListParams } from "@similarweb/pro-api-client";
import { apiFormat } from "@similarweb/pro-logic-components";

import type { IComponent } from "app/@types/ISwSettings";
import { isHidden as checkIsHidden, isLocked as checkIsLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { useApiQuery } from "app/modules/api-client";

export type ConversionRateAvailabilityState = "available" | "locked" | "hidden";

export interface IConversionRateAvailability {
    /** Widget availability kind */
    state: ConversionRateAvailabilityState;
    /** Loading state while checking availability */
    isLoading: boolean;
}

const CONVERSION_RATE_COMPONENT_KEY = "WebConversionRates";

interface IUseConversionRateAvailabilityParams {
    keys?: string[];
    from?: string;
    to?: string;
    includeSubdomains?: boolean;
    isWWW?: boolean;
}

export const useConversionRateAvailability = (
    params?: IUseConversionRateAvailabilityParams,
): IConversionRateAvailability => {
    const { keys, includeSubdomains, isWWW } = params ?? {};
    const includeSubdomainsValue = includeSubdomains ?? (typeof isWWW === "boolean" ? !isWWW : undefined);

    const component = (swSettings.components as Record<string, IComponent>)[CONVERSION_RATE_COMPONENT_KEY];

    const permissionCheckState = useMemo<ConversionRateAvailabilityState>(() => {
        if (!component) {
            return "hidden";
        }

        if (checkIsHidden(component)) {
            return "hidden";
        }

        if (checkIsLocked(component)) {
            return "locked";
        }

        return "available";
    }, [component]);

    const from = component.resources.SnapshotInterval.startdate;
    const to = component.resources.SnapshotInterval.enddate;

    // Probe the latest month to determine ecommerce eligibility.
    // Fetch for both "available" and "locked" states to avoid showing widgets for non-ecommerce domains.
    const shouldCheckDataAvailability =
        permissionCheckState !== "hidden" && !!(keys?.length && includeSubdomainsValue !== undefined);

    const {
        data: countriesData,
        isLoading,
        isFetching,
        isFetched,
        isError,
    } = useApiQuery(
        {
            tag: "WebConversionRatesCountries",
            methodName: "conversionRatesCountriesList",
            options: {
                enabled: shouldCheckDataAvailability,
                placeholderData: undefined,
            },
        },
        {
            keys: keys,
            from: dayjs.utc(from).format(apiFormat),
            to: dayjs.utc(to).format(apiFormat),
            includeSubdomains: includeSubdomainsValue,
        } satisfies ConversionRatesCountriesListParams,
    );

    const hasEcommerceData = !!countriesData?.some(({ countries }) => countries.length > 0);

    // If loading or refetching, return current permission state with loading flag.
    // This guards against stale cache reads during component remounts (e.g. when
    // toggling POP mode triggers a container switch and react-query returns cached
    // data while simultaneously running a background refetch).
    if ((isLoading || isFetching) && shouldCheckDataAvailability) {
        return { state: permissionCheckState, isLoading: true };
    }

    // If hidden by permissions, never fetch or render anything
    if (permissionCheckState === "hidden") {
        return { state: "hidden", isLoading: false };
    }

    // While countries data is still loading, report hidden so containers don't allocate
    // grid space for a widget that may end up not being shown (non-ecommerce domains).
    if (isLoading && shouldCheckDataAvailability) {
        return { state: "hidden", isLoading: true };
    }

    // If we didn't check data availability (no params provided), return permission state only
    if (!shouldCheckDataAvailability) {
        return { state: permissionCheckState, isLoading: false };
    }

    // Once countries data is fetched, derive final state from permission + ecommerce data.
    // Guard against undefined data during component remounts where isFetched can be true
    // before the subscription delivers cached data.
    if (isFetched && !isError && countriesData != null) {
        if (permissionCheckState === "locked") {
            // Locked: show locked widget only for ecommerce domains, otherwise hide
            return { state: hasEcommerceData ? "locked" : "hidden", isLoading: false };
        }

        // Available: show data widget for ecommerce domains, otherwise hide completely
        return { state: hasEcommerceData ? "available" : "hidden", isLoading: false };
    }

    return { state: permissionCheckState, isLoading: false };
};
